// Swiper Studio -- canvas slide model shared by the renderer (<DynamicSwiper>
// / SwiperCanvasSlide) and the Studio editor.
//
// Mirror of server_elakay/elakay_helpers/dynamicSwiperCanvas.js (enums,
// limits). The backend re-validates everything; this copy only lets the UI
// build valid compositions and render them exactly like the live pages.
//
// Units: element positions / sizes are PERCENTAGES of the frame; font
// sizes, paddings, radii, letter spacing are "design pixels" of a 1000 px
// wide frame, rendered with container units (cqi) so the composition scales
// with the real frame (page column, phone, scaled editor preview) instead of
// overflowing it.
import { isSafeButtonUrl } from "./dynamicSwiperRegistry";
import { elakayAutoText } from "../pageInside/dataElakay";


export const CANVAS_LIMITS = Object.freeze({
  MAX_ELEMENTS_PER_SLIDE: 20,
  ELEMENT_TEXT_MAX: 500,
  ELEMENT_NAME_MAX: 60,
  POSITION_MIN: -50,
  POSITION_MAX: 150,
  SIZE_MIN: 0.5,
  SIZE_MAX: 200,
  FONT_SIZE_MIN: 6,
  FONT_SIZE_MAX: 300,
  MAX_GRADIENT_STOPS: 5,
  MAX_VIDEO_MB: 10,
  MAX_VIDEO_BYTES: 10 * 1024 * 1024,
  ANIMATION_DURATION_MIN: 100,
  ANIMATION_DURATION_MAX: 5000,
  ANIMATION_DELAY_MAX: 10000,
  CANVAS_DIMENSION_MIN: 100,
  CANVAS_DIMENSION_MAX: 4000,
  EFFECT_DEPTH_MAX: 40,
  EFFECT_ANGLE_MIN: -180,
  EFFECT_ANGLE_MAX: 180,
});

export const DEVICES = Object.freeze(["desktop", "tablet", "mobile"]);
export const BACKGROUND_TYPES = Object.freeze(["transparent", "color", "gradient", "image", "video"]);
export const ELEMENT_TYPES = Object.freeze(["text", "image", "button", "shape", "video"]);
export const TEXT_ALIGNS = Object.freeze(["left", "center", "right"]);
export const TEXT_TRANSFORMS = Object.freeze(["none", "uppercase", "lowercase", "capitalize"]);
export const FONT_WEIGHTS = Object.freeze([300, 400, 500, 600, 700, 800, 900]);
export const SHADOWS = Object.freeze(["none", "sm", "md", "lg", "glow"]);
export const SHAPES = Object.freeze(["rect", "ellipse", "pill", "line"]);
// CSS-only text effects of a text element ("none" = normal 2D).
export const TEXT_EFFECTS = Object.freeze(["none", "shadow", "outline", "gradient", "glow", "extrude", "perspective"]);
// "auto": one layout scaled to every screen; "custom": Tablet / Mobile
// shapes and per-device positions apply (missing = "custom").
export const RESPONSIVE_MODES = Object.freeze(["auto", "custom"]);
// Design width of Tablet / Mobile when they follow Desktop automatically.
export const AUTO_DEVICE_WIDTHS = Object.freeze({ tablet: 768, mobile: 390 });
export const OBJECT_POSITIONS = Object.freeze(["center", "top", "bottom", "left", "right"]);
export const ACTION_TYPES = Object.freeze(["none", "url", "page", "product", "shop", "category"]);
export const ANIMATION_EASINGS = Object.freeze(["ease", "ease-in", "ease-out", "ease-in-out", "linear", "spring"]);
export const ANIMATION_TRIGGERS = Object.freeze(["slide", "load"]);
// "Entrance" presets play `repeat` times; "loop" presets default to forever.
export const ENTRANCE_ANIMATIONS = Object.freeze([
  "fadeIn", "fadeUp", "fadeDown", "fadeLeft", "fadeRight",
  "slideUp", "slideDown", "slideLeft", "slideRight",
  "zoomIn", "zoomOut", "bounce", "flip", "rotate",
]);
export const LOOP_ANIMATIONS = Object.freeze(["pulse", "float"]);
export const ANIMATION_TYPES = Object.freeze(["none", ...ENTRANCE_ANIMATIONS, ...LOOP_ANIMATIONS]);

// Font KEYS stored in the DB (never raw CSS) -> font stacks.
export const FONT_STACKS = Object.freeze({
  default: "inherit",
  sans: "'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', Times, serif",
  mono: "'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace",
  display: "'Poppins', 'Montserrat', 'DM Sans', system-ui, sans-serif",
  condensed: "'Oswald', 'Arial Narrow', 'Roboto Condensed', sans-serif",
  rounded: "'Nunito', 'Quicksand', 'Varela Round', system-ui, sans-serif",
  script: "'Pacifico', 'Brush Script MT', 'Segoe Script', cursive",
});
export const FONT_FAMILIES = Object.freeze(Object.keys(FONT_STACKS));

const SHADOW_CSS = Object.freeze({
  none: "none",
  sm: "0 2px 6px rgba(15, 23, 42, 0.18)",
  md: "0 8px 20px rgba(15, 23, 42, 0.25)",
  lg: "0 18px 40px rgba(15, 23, 42, 0.35)",
  glow: "0 0 24px rgba(255, 255, 255, 0.55)",
});
const TEXT_SHADOW_CSS = Object.freeze({
  none: "none",
  sm: "0 1px 3px rgba(0, 0, 0, 0.35)",
  md: "0 2px 8px rgba(0, 0, 0, 0.45)",
  lg: "0 4px 18px rgba(0, 0, 0, 0.55)",
  glow: "0 0 14px rgba(255, 255, 255, 0.8)",
});
const OBJECT_POSITION_CSS = Object.freeze({
  center: "50% 50%", top: "50% 0%", bottom: "50% 100%", left: "0% 50%", right: "100% 50%",
});
const EASING_CSS = Object.freeze({
  ease: "ease", "ease-in": "ease-in", "ease-out": "ease-out", "ease-in-out": "ease-in-out", linear: "linear",
  spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
});

export const DEFAULT_CANVAS_FRAME = Object.freeze({ width: 1600, height: 500 });

// Frame shapes offered in the Studio (width x height, 1:2 .. 8:1).
export const FRAME_PRESETS = Object.freeze([
  { id: "banner-16x5", width: 1600, height: 500, get label() { return elakayAutoText("h_ac280143835294", "16:5 banner"); } },
  { id: "banner-3x1", width: 1500, height: 500, get label() { return elakayAutoText("h_2f72af6e0532ce", "3:1 banner"); } },
  { id: "banner-4x1", width: 1600, height: 400, get label() { return elakayAutoText("h_a0b0abbfc1ce78", "4:1 thin banner"); } },
  { id: "wide-21x9", width: 2100, height: 900, get label() { return elakayAutoText("h_1ba8d1ab527787", "21:9 cinema"); } },
  { id: "hero-16x9", width: 1600, height: 900, get label() { return elakayAutoText("h_5f4a12a6b6a246", "16:9 hero"); } },
  { id: "classic-4x3", width: 1200, height: 900, label: "4:3" },
  { id: "square-1x1", width: 1000, height: 1000, get label() { return elakayAutoText("h_ecca23b207935f", "1:1 square"); } },
  { id: "portrait-4x5", width: 800, height: 1000, get label() { return elakayAutoText("h_cf68ee070740d9", "4:5 portrait"); } },
  { id: "story-9x16", width: 500, height: 1000, get label() { return elakayAutoText("h_4476951f021a5b", "1:2 story"); } },
]);

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
};
const round2 = (value) => Math.round(value * 100) / 100;

let idSequence = 0;
export const newElementId = (prefix = "el") => `${prefix}${Date.now().toString(36)}${(idSequence += 1).toString(36)}`.slice(0, 40);

// ─── Defaults ────────────────────────────────────────────────────────────

export const defaultStyle = (type) => ({
  fontFamily: type === "button" ? "sans" : "default",
  fontSize: type === "button" ? 22 : 32,
  fontWeight: type === "text" ? 700 : 600,
  italic: false,
  underline: false,
  color: type === "button" ? "#0f172a" : "#ffffff",
  backgroundColor: type === "button" ? "#ffffff" : type === "shape" ? "#ffffff" : "transparent",
  opacity: type === "shape" ? 0.9 : 1,
  textAlign: type === "button" ? "center" : "left",
  lineHeight: 1.2,
  letterSpacing: 0,
  textTransform: "none",
  paddingX: type === "button" ? 28 : 0,
  paddingY: type === "button" ? 14 : 0,
  borderRadius: type === "button" ? 999 : type === "image" ? 12 : 0,
  borderWidth: 0,
  borderColor: "#ffffff",
  shadow: type === "button" ? "md" : "none",
  objectFit: "contain",
  objectPosition: "center",
  hoverBackgroundColor: null,
  hoverColor: null,
  shape: type === "shape" ? "rect" : "rect",
  textEffect: "none",
  effectColor: "#000000",
  effectColor2: "#f97316",
  effectDepth: 6,
  effectAngle: 20,
});

export const defaultAnimation = () => ({ type: "none", duration: 600, delay: 0, easing: "ease-out", repeat: 1, trigger: "slide" });
export const defaultAction = () => ({ type: "none", url: "", target: "_self", wholeSlide: false });
export const defaultBackground = () => ({
  type: "gradient",
  color: "#0f172a",
  gradient: { kind: "linear", angle: 135, stops: [{ color: "#1e3a8a", position: 0 }, { color: "#7c3aed", position: 100 }] },
  media: null,
  fit: "cover",
  position: "center",
  overlayColor: "#000000",
  overlayOpacity: 0,
});

const DEFAULT_BOXES = {
  text: { x: 6, y: 18, w: 50, h: null },
  button: { x: 6, y: 68, w: 18, h: null },
  image: { x: 62, y: 12, w: 28, h: 76 },
  video: { x: 55, y: 10, w: 38, h: 80 },
  shape: { x: 40, y: 30, w: 20, h: 40 },
};

export const createElement = (type, overrides = {}) => {
  const base = {
    id: newElementId(type.slice(0, 2)),
    type,
    name: "",
    text: type === "text" ? "Your text" : type === "button" ? "SHOP NOW" : "",
    alt: "",
    media: null,
    style: defaultStyle(type),
    layout: { desktop: { ...DEFAULT_BOXES[type], rotation: 0, fontSize: null, textAlign: null, visible: true }, tablet: {}, mobile: {} },
    animation: defaultAnimation(),
    action: defaultAction(),
    video: type === "video" ? { autoplay: true, muted: true, loop: true, controls: false } : null,
    hidden: false,
    locked: false,
  };
  return {
    ...base,
    ...overrides,
    style: { ...base.style, ...(overrides.style || {}) },
    layout: {
      desktop: { ...base.layout.desktop, ...(overrides.layout?.desktop || {}) },
      tablet: { ...(overrides.layout?.tablet || {}) },
      mobile: { ...(overrides.layout?.mobile || {}) },
    },
    animation: { ...base.animation, ...(overrides.animation || {}) },
    action: { ...base.action, ...(overrides.action || {}) },
  };
};

export const createCanvasSlide = (overrides = {}) => ({
  layout: "canvas",
  alt: "",
  background: { ...defaultBackground(), ...(overrides.background || {}) },
  elements: overrides.elements || [],
  action: { ...defaultAction(), ...(overrides.action || {}) },
});

// ─── Normalization (old and new slides -> one shape) ─────────────────────

export const isCanvasSlide = (slide) => slide?.layout === "canvas";

// Any element as stored / received -> a complete element (missing parts
// filled with defaults). Never throws: the renderer must survive old or
// partial data.
export const normalizeElement = (element) => {
  if (!element || typeof element !== "object") return null;
  const type = ELEMENT_TYPES.includes(element.type) ? element.type : null;
  if (!type) return null;
  const base = createElement(type);
  return {
    ...base,
    ...element,
    id: String(element.id || base.id),
    style: { ...base.style, ...(element.style || {}) },
    layout: {
      desktop: { ...base.layout.desktop, ...(element.layout?.desktop || {}) },
      tablet: { ...(element.layout?.tablet || {}) },
      mobile: { ...(element.layout?.mobile || {}) },
    },
    animation: { ...base.animation, ...(element.animation || {}) },
    action: { ...base.action, ...(element.action || {}) },
  };
};

export const normalizeSlide = (slide) => {
  if (!slide || typeof slide !== "object") return null;
  if (!isCanvasSlide(slide)) return { ...slide, layout: "image" };
  return {
    ...slide,
    layout: "canvas",
    alt: slide.alt || "",
    background: { ...defaultBackground(), ...(slide.background || {}) },
    elements: (Array.isArray(slide.elements) ? slide.elements : []).map(normalizeElement).filter(Boolean),
    action: { ...defaultAction(), ...(slide.action || {}) },
  };
};

// Legacy image slide -> editable canvas composition: the image becomes the
// background, title / subtitle / description / button become real elements
// at the same place. Used by "Convert to canvas" in the editor; stored
// slides are never migrated automatically.
const POSITION_BOXES = {
  left: 6, center: 25, right: 44,
  top: 8, middle: 34, bottom: 56,
};
export const legacySlideToCanvas = (slide) => {
  const [vertical, horizontal] = String(slide?.contentPosition || "bottom-left").includes("-")
    ? String(slide.contentPosition).split("-")
    : ["center", "center"];
  const x = POSITION_BOXES[horizontal] ?? POSITION_BOXES.left;
  let y = POSITION_BOXES[vertical === "center" ? "middle" : vertical] ?? POSITION_BOXES.bottom;
  const align = horizontal === "center" ? "center" : horizontal === "right" ? "right" : "left";
  const dark = slide?.textTheme === "dark";
  const color = dark ? "#0f172a" : "#ffffff";
  const elements = [];
  const addText = (value, fontSize, weight, height) => {
    if (!value) return;
    elements.push(createElement("text", {
      text: value,
      style: { fontSize, fontWeight: weight, color, textAlign: align, shadow: dark ? "none" : "sm" },
      layout: { desktop: { x, y, w: 50, h: null } },
    }));
    y += height;
  };
  addText(slide?.title, 48, 800, 13);
  addText(slide?.subtitle, 26, 600, 9);
  addText(slide?.description, 18, 400, 10);
  if (slide?.button?.enabled && slide.button.label) {
    elements.push(createElement("button", {
      text: slide.button.label,
      action: { type: "url", url: slide.button.url || "", target: slide.button.target || "_self" },
      layout: { desktop: { x, y: Math.min(y + 2, 84), w: 20 } },
    }));
  }
  const hasImage = Boolean(slide?.imageUrl);
  return createCanvasSlide({
    background: hasImage
      ? {
        type: "image",
        media: { kind: "image", url: slide.imageUrl, width: slide.width || null, height: slide.height || null, mimeType: "" },
        fit: slide.imageFit === "contain" ? "contain" : "cover",
        position: OBJECT_POSITIONS.includes(slide.imagePosition) ? slide.imagePosition : "center",
        overlayOpacity: slide.overlay === "full" ? 0.35 : 0,
      }
      : defaultBackground(),
    elements,
  });
};

// ─── Responsive resolution ───────────────────────────────────────────────

// Desktop is complete; Tablet / Mobile only store what they override.
export const resolveElementBox = (element, device = "desktop") => {
  const desktop = element?.layout?.desktop || {};
  const override = device === "desktop" ? {} : (element?.layout?.[device] || {});
  const pick = (key) => (override[key] !== undefined && override[key] !== null ? override[key] : desktop[key]);
  return {
    x: clamp(pick("x"), CANVAS_LIMITS.POSITION_MIN, CANVAS_LIMITS.POSITION_MAX, 10),
    y: clamp(pick("y"), CANVAS_LIMITS.POSITION_MIN, CANVAS_LIMITS.POSITION_MAX, 10),
    w: clamp(pick("w"), CANVAS_LIMITS.SIZE_MIN, CANVAS_LIMITS.SIZE_MAX, 30),
    h: pick("h") === null || pick("h") === undefined ? null : clamp(pick("h"), CANVAS_LIMITS.SIZE_MIN, CANVAS_LIMITS.SIZE_MAX, null),
    rotation: clamp(pick("rotation"), -180, 180, 0),
    fontSize: pick("fontSize") ?? null,
    textAlign: TEXT_ALIGNS.includes(pick("textAlign")) ? pick("textAlign") : null,
    visible: pick("visible") !== false,
  };
};

// Which properties of this device are overridden (for the Studio badges).
export const overriddenKeys = (element, device) => (device === "desktop"
  ? []
  : Object.entries(element?.layout?.[device] || {}).filter(([, value]) => value !== undefined && value !== null).map(([key]) => key));

// Which device's layout is drawn: in "auto" responsive mode every screen
// shows the Desktop layout, scaled.
export const layoutDeviceFor = (settings, device) => (settings?.responsiveMode === "auto" ? "desktop" : device);

// Frame shape for a device (tablet / mobile may use their own shape, unless
// the swiper is in "auto" responsive mode).
export const frameRatioFor = (swiper, device) => {
  const settings = swiper?.settings || {};
  const layoutDevice = layoutDeviceFor(settings, device);
  const override = layoutDevice === "mobile" ? settings.mobileRatio : layoutDevice === "tablet" ? settings.tabletRatio : null;
  if (override?.width && override?.height) return { width: override.width, height: override.height };
  const width = Number(swiper?.aspectRatio?.width) || 0;
  const height = Number(swiper?.aspectRatio?.height) || 0;
  if (width && height) return { width, height };
  const canvas = settings.canvas;
  if (canvas?.width && canvas?.height) return { width: canvas.width, height: canvas.height };
  return null;
};

// Real design size (px) of a device's frame, from the Desktop frame:
// Desktop = the swiper's own size; Tablet / Mobile = their custom size, or
// the Desktop shape at the usual device width (768 / 390 px).
export const deviceDesignFrame = (desktopFrame, settings, device) => {
  const base = desktopFrame?.width && desktopFrame?.height ? desktopFrame : DEFAULT_CANVAS_FRAME;
  if (device === "desktop" || !AUTO_DEVICE_WIDTHS[device]) return { width: base.width, height: base.height, auto: false };
  const override = layoutDeviceFor(settings, device) === device
    ? (device === "mobile" ? settings?.mobileRatio : settings?.tabletRatio)
    : null;
  if (override?.width && override?.height) return { width: override.width, height: override.height, auto: false };
  const width = AUTO_DEVICE_WIDTHS[device];
  return { width, height: Math.max(1, Math.round((width * base.height) / base.width)), auto: true };
};

// Design pixels (1000 px wide frame) <-> real pixels of a frame `width` px
// wide: the Studio shows and edits sizes in real pixels.
export const designToPx = (value, frameWidth) => round2(((Number(value) || 0) * (Number(frameWidth) || 1000)) / 1000);
export const pxToDesign = (value, frameWidth) => round2(((Number(value) || 0) * 1000) / (Number(frameWidth) || 1000));

// ─── CSS (the renderer and the Studio canvas use exactly these) ──────────

// "Design pixels" (1000 px wide frame) -> container units of the frame.
export const designUnit = (value) => `calc(${round2(Number(value) || 0)} * 0.1cqi)`;

export const backgroundCss = (background) => {
  const bg = background || {};
  if (bg.type === "transparent") return { background: "transparent" };
  if (bg.type === "gradient") {
    const stops = (bg.gradient?.stops || []).map((stop) => `${stop.color} ${stop.position}%`).join(", ");
    if (!stops) return { background: bg.color || "#0f172a" };
    return {
      background: bg.gradient?.kind === "radial"
        ? `radial-gradient(circle at center, ${stops})`
        : `linear-gradient(${Number(bg.gradient?.angle ?? 135)}deg, ${stops})`,
    };
  }
  // color, and the base color under an image / video while it loads
  return { background: bg.color || "#0f172a" };
};

// Text effect of a text element, CSS only (text-shadow, text stroke,
// background-clip, transform). null = normal 2D text.
export const textEffectCss = (style) => {
  const kind = style?.textEffect;
  if (!kind || kind === "none" || !TEXT_EFFECTS.includes(kind)) return null;
  const depth = clamp(style.effectDepth, 0, CANVAS_LIMITS.EFFECT_DEPTH_MAX, 6);
  const color = style.effectColor || "#000000";
  const angle = clamp(style.effectAngle, CANVAS_LIMITS.EFFECT_ANGLE_MIN, CANVAS_LIMITS.EFFECT_ANGLE_MAX, 20);
  const u = designUnit;
  if (kind === "shadow") return { textShadow: `${u(depth / 2)} ${u(depth / 2)} ${u(depth)} ${color}` };
  if (kind === "glow") return { textShadow: `0 0 ${u(depth)} ${color}, 0 0 ${u(depth * 2)} ${color}` };
  if (kind === "outline") return { WebkitTextStroke: `${u(Math.max(0.5, depth / 2))} ${color}`, paintOrder: "stroke fill" };
  if (kind === "gradient") {
    return {
      backgroundColor: "transparent",
      backgroundImage: `linear-gradient(${round2(90 + angle)}deg, ${style.color || "#ffffff"}, ${style.effectColor2 || "#f97316"})`,
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      WebkitTextFillColor: "transparent",
    };
  }
  if (kind === "extrude") {
    const steps = Math.min(20, Math.max(1, Math.round(depth)));
    const layers = Array.from({ length: steps }, (_, index) => `${u(index + 1)} ${u(index + 1)} 0 ${color}`);
    layers.push(`${u(steps + 2)} ${u(steps + 2)} ${u(steps)} rgba(0, 0, 0, 0.35)`);
    return { textShadow: layers.join(", ") };
  }
  // perspective: a tilted plane (never on the animated wrapper, so an
  // entrance animation keeps its own transform).
  const tilt = clamp(angle, -60, 60, 20);
  return {
    transform: `perspective(${u(900)}) rotateY(${tilt}deg) rotateX(${round2(tilt / 3)}deg)`,
    transformOrigin: "center",
    textShadow: `${u(2)} ${u(4)} ${u(8)} rgba(0, 0, 0, 0.35)`,
  };
};

export const objectPositionCss = (key) => OBJECT_POSITION_CSS[key] || OBJECT_POSITION_CSS.center;

export const elementBoxCss = (box) => ({
  left: `${box.x}%`,
  top: `${box.y}%`,
  width: `${box.w}%`,
  ...(box.h !== null && box.h !== undefined ? { height: `${box.h}%` } : {}),
  ...(box.rotation ? { transform: `rotate(${box.rotation}deg)` } : {}),
});

export const elementContentCss = (element, box) => {
  const style = element?.style || {};
  const type = element?.type;
  const css = {
    opacity: clamp(style.opacity, 0, 1, 1),
  };
  if (type === "text" || type === "button") {
    Object.assign(css, {
      fontFamily: FONT_STACKS[style.fontFamily] || FONT_STACKS.default,
      fontSize: designUnit(box?.fontSize ?? style.fontSize ?? 32),
      fontWeight: style.fontWeight || 400,
      fontStyle: style.italic ? "italic" : "normal",
      textDecoration: style.underline ? "underline" : "none",
      color: style.color || "#ffffff",
      textAlign: box?.textAlign || style.textAlign || "left",
      lineHeight: style.lineHeight || 1.2,
      letterSpacing: designUnit(style.letterSpacing || 0),
      textTransform: style.textTransform || "none",
      padding: `${designUnit(style.paddingY || 0)} ${designUnit(style.paddingX || 0)}`,
      textShadow: type === "text" && style.backgroundColor === "transparent" ? TEXT_SHADOW_CSS[style.shadow] || "none" : "none",
    });
  }
  if (type === "button") {
    // Through variables so the hover colors (stylesheet) can take over.
    delete css.color;
    css["--dsw-bg"] = style.backgroundColor || "transparent";
    css["--dsw-fg"] = style.color || "#ffffff";
  } else if (type !== "image" && type !== "video") {
    css.backgroundColor = style.backgroundColor || "transparent";
  }
  if (type === "shape") {
    css.borderRadius = style.shape === "ellipse" ? "50%" : style.shape === "pill" ? "9999px" : designUnit(style.borderRadius || 0);
  } else {
    css.borderRadius = designUnit(style.borderRadius || 0);
  }
  if (style.borderWidth) css.border = `${designUnit(style.borderWidth)} solid ${style.borderColor || "#ffffff"}`;
  if (type !== "text" || style.backgroundColor !== "transparent") css.boxShadow = SHADOW_CSS[style.shadow] || "none";
  if (type === "image" || type === "video") {
    css.objectFit = style.objectFit === "cover" ? "cover" : "contain";
    css.objectPosition = objectPositionCss(style.objectPosition);
  }
  if (type === "text") Object.assign(css, textEffectCss(style) || {});
  if (style.hoverBackgroundColor) css["--dsw-hover-bg"] = style.hoverBackgroundColor;
  if (style.hoverColor) css["--dsw-hover-color"] = style.hoverColor;
  return css;
};

// Animation of one element. Returned as inline CSS so the Studio preview
// and the live page share it; the keyframes live in DynamicSwipers.css.
export const animationCss = (animation) => {
  const anim = animation || {};
  if (!anim.type || anim.type === "none" || !ANIMATION_TYPES.includes(anim.type)) return null;
  const loop = LOOP_ANIMATIONS.includes(anim.type);
  const repeat = Number(anim.repeat) === 0 || (loop && anim.repeat === undefined) ? "infinite" : Math.max(1, Math.round(Number(anim.repeat) || 1));
  const duration = clamp(anim.duration, CANVAS_LIMITS.ANIMATION_DURATION_MIN, CANVAS_LIMITS.ANIMATION_DURATION_MAX, 600);
  const delay = clamp(anim.delay, 0, CANVAS_LIMITS.ANIMATION_DELAY_MAX, 0);
  return {
    animation: `dsw-anim-${anim.type} ${duration}ms ${EASING_CSS[anim.easing] || "ease-out"} ${delay}ms ${repeat} both`,
  };
};

// ─── Links ───────────────────────────────────────────────────────────────

export const isActiveAction = (action) => Boolean(action && action.type && action.type !== "none" && isSafeButtonUrl(action.url));
export const isInternalUrl = (url) => typeof url === "string" && url.startsWith("/") && !url.startsWith("//");

// Placeholders shown in the Studio for each action type.
export const ACTION_PLACEHOLDERS = Object.freeze({
  url: "https://...",
  page: "/deal-today",
  product: "/product/preview/p-...",
  shop: "/lakay-ShopSeller/u-...",
  category: "/product/c-...",
});

// First useful text of a slide (accessible name of a whole-slide link).
export const slideAccessibleName = (slide) => {
  if (slide?.alt) return slide.alt;
  const first = (slide?.elements || []).find((element) => (element.type === "text" || element.type === "button") && element.text);
  return first?.text || "";
};

// ─── Templates ───────────────────────────────────────────────────────────

const t = (text, style, desktop, extra = {}) => createElement("text", { text, style, layout: { desktop, ...(extra.layout || {}) }, ...extra });
const b = (text, desktop, extra = {}) => createElement("button", { text, layout: { desktop, ...(extra.layout || {}) }, ...extra });
const shape = (style, desktop, extra = {}) => createElement("shape", { style, layout: { desktop, ...(extra.layout || {}) }, ...extra });
const fade = (type, delay) => ({ animation: { type, duration: 600, delay, easing: "ease-out", repeat: 1, trigger: "slide" } });

const linkPreviewBrandElements = (headline = "elakay", slogan = "ALL IN ONE. ONE FOR ALL") => [
  shape({ backgroundColor: "#ffffff", opacity: 0.95, shape: "rect", borderRadius: 3 }, { x: 5.5, y: 31, w: 3.2, h: 10, rotation: 45 }, { name: "Brand diamond" }),
  t(headline, { fontSize: 60, fontWeight: 800, color: "#ffffff", fontFamily: "sans" }, { x: 10, y: 25, w: 40 }, { name: "Brand name" }),
  t(slogan, { fontSize: 19, fontWeight: 700, color: "#ffffff", letterSpacing: 3 }, { x: 6, y: 54, w: 46 }, { name: "Slogan" }),
];

const linkPreviewImageElement = (url, name, desktop, { width = 1200, height = 630, mimeType = "image/png", fit = "cover" } = {}) => createElement("image", {
  name,
  alt: name,
  media: { kind: "image", url, width, height, mimeType },
  style: { objectFit: fit, objectPosition: "center", borderRadius: 0 },
  layout: { desktop },
});

const linkPreviewPhotoTemplate = (id, label, path, { width = 1200, height = 630, mimeType = "image/png" } = {}) => ({
  id: `link-preview-${id}`,
  label,
  group: "link-preview",
  build: () => createCanvasSlide({
    alt: label,
    background: {
      type: "gradient",
      color: "#062c3c",
      gradient: { kind: "linear", angle: 110, stops: [{ color: "#041f2c", position: 0 }, { color: "#164e63", position: 100 }] },
    },
    elements: [
      linkPreviewImageElement(path, `${label} photo`, { x: 44, y: 0, w: 56, h: 100 }, { width, height, mimeType }),
      shape({ backgroundColor: "#062c3c", opacity: 0.68, shape: "rect" }, { x: 0, y: 0, w: 44, h: 100 }, { name: "Text contrast panel" }),
      ...linkPreviewBrandElements(),
    ],
  }),
});

const linkPreviewExactPhotoTemplate = (id, label, path) => ({
  id: `link-preview-exact-${id}`,
  label,
  group: "link-preview-featured",
  build: () => createCanvasSlide({
    alt: label,
    background: { type: "color", color: "#071f2c" },
    elements: [
      linkPreviewImageElement(path, `${label} original image`, { x: 0, y: 0, w: 100, h: 100 }, { width: 1200, height: 675 }),
      shape({ backgroundColor: "#071f2c", opacity: 0.94, shape: "rect" }, { x: 0, y: 0, w: 46, h: 100 }, { name: "Editable text panel" }),
      ...linkPreviewBrandElements(),
    ],
  }),
});

const linkPreviewExactOfficialTemplate = (id, label, path, headline, subtitle) => ({
  id: `link-preview-exact-${id}`,
  label,
  group: "link-preview-featured",
  build: () => createCanvasSlide({
    alt: label,
    background: { type: "color", color: "#05255a" },
    elements: [
      linkPreviewImageElement(path, `${label} original image`, { x: 0, y: 0, w: 100, h: 100 }, { width: 1200, height: 630, mimeType: "image/jpeg" }),
      shape({ backgroundColor: "#073b82", opacity: 0.9, shape: "rect" }, { x: 40, y: 12, w: 57, h: 76 }, { name: "Editable text panel" }),
      t(headline, { fontSize: id === "slogan" ? 52 : 74, fontWeight: 900, color: "#ffffff", fontFamily: "sans", lineHeight: 0.95 }, { x: 43, y: id === "slogan" ? 27 : 31, w: 52 }, { name: "Main text" }),
      t(subtitle, { fontSize: 20, fontWeight: 600, color: "#ffffff", letterSpacing: 5 }, { x: 44, y: 65, w: 48 }, { name: "Subtitle" }),
    ],
  }),
});

const linkPreviewColorTemplate = (id, label, colors) => ({
  id: `link-preview-${id}`,
  label,
  group: "link-preview",
  build: () => createCanvasSlide({
    alt: label,
    background: {
      type: "gradient",
      color: colors[0],
      gradient: {
        kind: "linear",
        angle: 115,
        stops: colors.map((color, index) => ({ color, position: Math.round((index * 100) / (colors.length - 1)) })),
      },
    },
    elements: [
      shape({ backgroundColor: "#ffffff", opacity: 0.1, shape: "ellipse" }, { x: 70, y: -38, w: 42, h: 76 }, { name: "Top light circle" }),
      shape({ backgroundColor: "#ffffff", opacity: 0.08, shape: "ellipse" }, { x: 60, y: 52, w: 38, h: 68 }, { name: "Bottom light circle" }),
      shape({ backgroundColor: "#ffffff", opacity: 0.16, shape: "rect" }, { x: 0, y: 80, w: 100, h: 20 }, { name: "Footer band" }),
      ...linkPreviewBrandElements(),
    ],
  }),
});

// The eight cards highlighted in Settings > Link Preview, rebuilt as Canvas
// compositions. The photos stay as backgrounds; every color panel, shape,
// brand line and slogan is a normal editable layer.
const linkPreviewFeaturedTemplate = (id, label, template) => ({
  ...template,
  id: `link-preview-featured-${id}`,
  label,
  group: "link-preview-featured",
});

export const LINK_PREVIEW_ORIGINAL_IMAGE_TEMPLATES = Object.freeze([
  linkPreviewFeaturedTemplate("marketplace", "Elakay Marketplace — editable", linkPreviewExactOfficialTemplate("marketplace", "", "/elakay-social-preview.jpg", "Elakay", "Marketplace")),
  linkPreviewFeaturedTemplate("slogan", "ALL IN ONE. ONE FOR ALL — editable", linkPreviewExactOfficialTemplate("slogan", "", "/elakay-social-preview-slogan.jpg", "ALL IN ONE.\nONE FOR ALL", "Elakay Marketplace")),
  linkPreviewFeaturedTemplate("global", "Global marketplace — editable", linkPreviewExactPhotoTemplate("global", "Global marketplace", "/social-preview-global-photo.png")),
  linkPreviewFeaturedTemplate("caribbean", "Caribbean marketplace — editable", linkPreviewExactPhotoTemplate("caribbean", "Caribbean marketplace", "/social-preview-caribbean-photo.png")),
  linkPreviewFeaturedTemplate("future", "Future commerce — editable", linkPreviewExactPhotoTemplate("future", "Future commerce", "/social-preview-future-photo.png")),
  linkPreviewFeaturedTemplate("artisan", "Artisan market — editable", linkPreviewExactPhotoTemplate("artisan", "Artisan market", "/social-preview-artisan-photo.png")),
  linkPreviewFeaturedTemplate("delivery", "Delivery & commerce — editable", linkPreviewExactPhotoTemplate("delivery", "Delivery & commerce", "/social-preview-delivery-photo.png")),
  linkPreviewFeaturedTemplate("luxury", "Luxury marketplace — editable", linkPreviewExactPhotoTemplate("luxury", "Luxury marketplace", "/social-preview-luxury-photo.png")),
]);

export const LINK_PREVIEW_SLIDE_TEMPLATES = Object.freeze([
  linkPreviewColorTemplate("marketplace", "Elakay Marketplace", ["#071f2c", "#007399", "#24c6dc"]),
  linkPreviewColorTemplate("slogan", "ALL IN ONE. ONE FOR ALL", ["#0f172a", "#334155", "#007399"]),
  linkPreviewPhotoTemplate("global", "Global marketplace", "/social-preview-global.png"),
  linkPreviewPhotoTemplate("caribbean", "Caribbean marketplace", "/social-preview-caribbean.png"),
  linkPreviewPhotoTemplate("future", "Future commerce", "/social-preview-future.png"),
  linkPreviewPhotoTemplate("artisan", "Artisan market", "/social-preview-artisan.png"),
  linkPreviewPhotoTemplate("delivery", "Delivery & commerce", "/social-preview-delivery.png"),
  linkPreviewPhotoTemplate("luxury", "Luxury marketplace", "/social-preview-luxury.png"),
  linkPreviewColorTemplate("color-ocean", "Ocean blue", ["#04395e", "#007399", "#24c6dc"]),
  linkPreviewColorTemplate("color-sunset", "Sunset orange", ["#7f1d1d", "#f97316", "#facc15"]),
  linkPreviewColorTemplate("color-orchid", "Orchid violet", ["#312e81", "#7c3aed", "#ec4899"]),
  linkPreviewColorTemplate("color-forest", "Forest green", ["#052e16", "#047857", "#84cc16"]),
  linkPreviewColorTemplate("color-midnight", "Midnight blue", ["#071a35", "#173f73", "#2878b8"]),
  linkPreviewColorTemplate("color-rose", "Modern rose", ["#781d42", "#c63f6e", "#f49bb5"]),
  linkPreviewColorTemplate("color-gold", "Warm gold", ["#6a4212", "#c88a22", "#f3d47b"]),
  linkPreviewColorTemplate("color-sky", "Sky blue", ["#075985", "#0ea5e9", "#a5e8ff"]),
  linkPreviewColorTemplate("color-coral", "Soft coral", ["#8b2f2b", "#ef6b5b", "#ffc2a8"]),
  linkPreviewColorTemplate("color-slate", "Slate gray", ["#17212f", "#475569", "#a8b3c3"]),
]);

export const SLIDE_TEMPLATES = Object.freeze([
  {
    id: "blank",
    get label() { return elakayAutoText("h_276c9361fff15e", "Blank"); },
    group: "start",
    build: () => createCanvasSlide({ background: { type: "color", color: "#0f172a" } }),
  },
  {
    id: "text",
    get label() { return elakayAutoText("h_34f00a2661422c", "Text slide"); },
    group: "start",
    build: () => createCanvasSlide({
      background: { type: "color", color: "#1d4ed8" },
      elements: [
        t("Your headline", { fontSize: 64, fontWeight: 800, textAlign: "center" }, { x: 10, y: 28, w: 80 }, fade("fadeUp", 0)),
        t("A short line of text", { fontSize: 26, fontWeight: 500, textAlign: "center" }, { x: 15, y: 58, w: 70 }, fade("fadeUp", 150)),
      ],
    }),
  },
  {
    id: "sale",
    get label() { return elakayAutoText("h_0028d743eb44b8", "Sale"); },
    build: () => createCanvasSlide({
      background: { type: "gradient", gradient: { kind: "linear", angle: 120, stops: [{ color: "#f97316", position: 0 }, { color: "#db2777", position: 100 }] } },
      elements: [
        t("SUMMER SALE", { fontSize: 72, fontWeight: 900, fontFamily: "display", shadow: "sm" }, { x: 6, y: 16, w: 60 }, fade("fadeUp", 0)),
        t("UP TO 40% OFF", { fontSize: 34, fontWeight: 700 }, { x: 6, y: 46, w: 55 }, { ...fade("fadeUp", 150), layout: { mobile: { y: 44, fontSize: 44 } } }),
        b("SHOP NOW", { x: 6, y: 68, w: 18 }, { ...fade("fadeUp", 400), layout: { mobile: { w: 40, y: 72 } } }),
        shape({ backgroundColor: "#ffffff", opacity: 0.15, shape: "ellipse" }, { x: 68, y: -20, w: 40, h: 140 }, { name: "Decoration", layout: { mobile: { visible: false } } }),
      ],
    }),
  },
  {
    id: "product",
    get label() { return elakayAutoText("h_16df62a461a75f", "Product promotion"); },
    build: () => createCanvasSlide({
      background: { type: "color", color: "#fff7ed" },
      elements: [
        t("NEW ARRIVAL", { fontSize: 20, fontWeight: 800, color: "#ea580c", letterSpacing: 3 }, { x: 6, y: 16, w: 45 }, fade("fadeIn", 0)),
        t("Product name", { fontSize: 56, fontWeight: 800, color: "#0f172a" }, { x: 6, y: 28, w: 48 }, fade("fadeUp", 100)),
        t("$49.99", { fontSize: 36, fontWeight: 700, color: "#0f172a" }, { x: 6, y: 56, w: 30 }, fade("fadeUp", 250)),
        b("VIEW PRODUCT", { x: 6, y: 74, w: 22 }, { style: { backgroundColor: "#0f172a", color: "#ffffff" }, ...fade("fadeUp", 400) }),
        t("Add the product image →", { fontSize: 18, fontWeight: 500, color: "#9a3412", textAlign: "center" }, { x: 58, y: 45, w: 34 }, { name: "Replace me with an image" }),
      ],
    }),
  },
  {
    id: "shop",
    get label() { return elakayAutoText("h_64735d7dadf59e", "Shop promotion"); },
    build: () => createCanvasSlide({
      background: { type: "gradient", gradient: { kind: "linear", angle: 90, stops: [{ color: "#0f766e", position: 0 }, { color: "#115e59", position: 100 }] } },
      elements: [
        t("Discover our shop", { fontSize: 54, fontWeight: 800 }, { x: 6, y: 22, w: 50 }, fade("fadeUp", 0)),
        t("New styles available now", { fontSize: 24, fontWeight: 500 }, { x: 6, y: 50, w: 45 }, fade("fadeUp", 150)),
        b("VISIT SHOP", { x: 6, y: 68, w: 18 }, fade("fadeUp", 300)),
      ],
    }),
  },
  {
    id: "announcement",
    get label() { return elakayAutoText("h_cf84a98d6cf7f1", "Announcement"); },
    build: () => createCanvasSlide({
      background: { type: "color", color: "#1d4ed8" },
      elements: [
        t("FREE SHIPPING THIS WEEKEND", { fontSize: 48, fontWeight: 800, textAlign: "center" }, { x: 10, y: 30, w: 80 }, fade("zoomIn", 0)),
        t("On every order over $50", { fontSize: 24, fontWeight: 500, textAlign: "center" }, { x: 15, y: 58, w: 70 }, fade("fadeUp", 200)),
      ],
    }),
  },
  {
    id: "hero",
    get label() { return elakayAutoText("h_004a61351ca138", "Hero banner"); },
    build: () => createCanvasSlide({
      background: { type: "gradient", gradient: { kind: "radial", angle: 0, stops: [{ color: "#312e81", position: 0 }, { color: "#020617", position: 100 }] } },
      elements: [
        t("NEW COLLECTION", { fontSize: 80, fontWeight: 900, fontFamily: "display", textAlign: "center", letterSpacing: 4 }, { x: 10, y: 28, w: 80 }, fade("fadeUp", 0)),
        b("EXPLORE", { x: 41, y: 64, w: 18 }, fade("fadeUp", 300)),
      ],
    }),
  },
  {
    id: "minimal",
    get label() { return elakayAutoText("h_a711cca9a4a9ca", "Minimal"); },
    build: () => createCanvasSlide({
      background: { type: "color", color: "#f8fafc" },
      elements: [
        t("Less, but better.", { fontSize: 60, fontWeight: 300, color: "#0f172a", fontFamily: "serif" }, { x: 8, y: 34, w: 70 }, fade("fadeIn", 0)),
      ],
    }),
  },
  {
    id: "split",
    get label() { return elakayAutoText("h_73f8c9f5116462", "Split layout"); },
    build: () => createCanvasSlide({
      background: { type: "color", color: "#ffffff" },
      elements: [
        shape({ backgroundColor: "#0f172a", opacity: 1, shape: "rect" }, { x: 50, y: 0, w: 50, h: 100 }, { name: "Right panel" }),
        t("Image + text", { fontSize: 52, fontWeight: 800, color: "#0f172a" }, { x: 6, y: 30, w: 40 }, fade("fadeRight", 0)),
        b("LEARN MORE", { x: 6, y: 62, w: 18 }, { style: { backgroundColor: "#0f172a", color: "#ffffff" }, ...fade("fadeUp", 250) }),
      ],
    }),
  },
  {
    id: "video",
    get label() { return elakayAutoText("h_aaa402bd9bb21e", "Video hero"); },
    group: "start",
    needsVideo: true,
    build: () => createCanvasSlide({
      background: { type: "color", color: "#000000", overlayColor: "#000000", overlayOpacity: 0.35 },
      elements: [
        t("New Collection", { fontSize: 72, fontWeight: 800, textAlign: "center" }, { x: 10, y: 32, w: 80 }, fade("fadeUp", 0)),
        b("EXPLORE", { x: 41, y: 62, w: 18 }, fade("fadeUp", 300)),
      ],
    }),
  },
  ...LINK_PREVIEW_SLIDE_TEMPLATES,
  ...LINK_PREVIEW_ORIGINAL_IMAGE_TEMPLATES,
]);

export const buildTemplateSlide = (templateId) => {
  const template = SLIDE_TEMPLATES.find((entry) => entry.id === templateId) || SLIDE_TEMPLATES[0];
  const slide = template.build();
  // Give every layer a readable name for the Layers panel.
  slide.elements = slide.elements.map((element, index) => ({
    ...element,
    name: element.name || (element.type === "text" ? element.text.slice(0, 24) : `${element.type} ${index + 1}`),
  }));
  return slide;
};

// Element shown when a template is converted with no media yet.
export const describeElement = (element) => {
  if (!element) return "";
  if (element.name) return element.name;
  if (element.type === "text" || element.type === "button") return (element.text || element.type).slice(0, 30);
  return element.type;
};

// Pixel <-> percent helpers used by the Studio canvas when dragging.
export const toPercent = (pixels, total) => (total ? round2((pixels / total) * 100) : 0);
export const clampBox = (box) => ({
  ...box,
  x: round2(clamp(box.x, CANVAS_LIMITS.POSITION_MIN, CANVAS_LIMITS.POSITION_MAX, 0)),
  y: round2(clamp(box.y, CANVAS_LIMITS.POSITION_MIN, CANVAS_LIMITS.POSITION_MAX, 0)),
  w: round2(clamp(box.w, CANVAS_LIMITS.SIZE_MIN, CANVAS_LIMITS.SIZE_MAX, 10)),
  ...(box.h === null || box.h === undefined ? {} : { h: round2(clamp(box.h, CANVAS_LIMITS.SIZE_MIN, CANVAS_LIMITS.SIZE_MAX, 10)) }),
});

// ─── Validation (what the server would refuse, explained before saving) ──

// Returns [{ elementId|null, code }] -- codes are translated by the UI.
export const validateCanvasSlide = (slide) => {
  const issues = [];
  const bg = slide?.background || {};
  if ((bg.type === "image" || bg.type === "video") && (!bg.media?.url || bg.media.kind !== bg.type)) {
    issues.push({ elementId: null, code: bg.type === "video" ? "BACKGROUND_VIDEO_MISSING" : "BACKGROUND_IMAGE_MISSING" });
  }
  const action = slide?.action;
  if (action && action.type && action.type !== "none" && !isSafeButtonUrl(action.url)) issues.push({ elementId: null, code: "SLIDE_LINK_INVALID" });
  const elements = Array.isArray(slide?.elements) ? slide.elements : [];
  if (elements.length > CANVAS_LIMITS.MAX_ELEMENTS_PER_SLIDE) issues.push({ elementId: null, code: "TOO_MANY_ELEMENTS" });
  elements.forEach((element) => {
    if ((element.type === "image" || element.type === "video") && !element.media?.url) issues.push({ elementId: element.id, code: "ELEMENT_MEDIA_MISSING" });
    if (element.type === "button" && !String(element.text || "").trim()) issues.push({ elementId: element.id, code: "BUTTON_TEXT_MISSING" });
    const elementAction = element.action;
    if (elementAction && elementAction.type && elementAction.type !== "none" && !isSafeButtonUrl(elementAction.url)) {
      issues.push({ elementId: element.id, code: "ELEMENT_LINK_INVALID" });
    }
  });
  return issues;
};

// Canvas slide -> exactly what the API stores (no editor-only fields).
export const serializeCanvasSlide = (slide) => ({
  layout: "canvas",
  alt: slide.alt || "",
  background: slide.background,
  elements: (slide.elements || []).map(({ previewSrc, ...element }) => element),
  action: slide.action,
});

// Deep copy with fresh element ids (duplicate slide / element).
export const cloneElement = (element) => ({
  ...JSON.parse(JSON.stringify(element)),
  id: newElementId(String(element.type || "el").slice(0, 2)),
  name: element.name ? `${element.name} copy`.slice(0, CANVAS_LIMITS.ELEMENT_NAME_MAX) : "",
});

// ─── Email (static representation) ───────────────────────────────────────

// Destination of ONE slide for an email-safe block (a static image can only
// carry one link): the slide link, else its first linked element, else a
// legacy slide's button.
export const slideLinkUrl = (slide) => {
  if (!slide) return "";
  if (isCanvasSlide(slide)) {
    if (isActiveAction(slide.action)) return slide.action.url;
    const linked = (slide.elements || []).find((element) => !element.hidden && isActiveAction(element.action));
    return linked ? linked.action.url : "";
  }
  return slide.button?.url && isSafeButtonUrl(slide.button.url) ? slide.button.url : "";
};

// "Swiper default URL": the first slide that has a destination.
export const swiperDefaultLinkUrl = (swiper) => {
  for (const slide of swiper?.slides || []) {
    const url = slideLinkUrl(slide);
    if (url) return url;
  }
  return "";
};

// Relative URL of the server-rendered static image of one slide.
export const swiperEmailImagePath = (swiperId, index = 0, width = 1200) => (
  /^[0-9a-fA-F]{24}$/.test(String(swiperId || ""))
    ? `/elakay_api/dynamic-swipers/public/swiper/${swiperId}/slides/${Math.max(0, Math.round(Number(index) || 0))}/email-image?w=${width}`
    : ""
);
